import ButtonSpinner from '../_common/components/buttons/buttonSpinner/buttonSpinner'
import MatriculacionModal from '../matriculacion-modal/matriculacion-modal'
import Modal from '../_common/components/Modal'
import crypto from 'crypto'
import { h, Fragment } from 'preact'
import { isSpanishUser } from '../_common/utils/getLang'
import { t } from '../_i18n'
import { toastService } from '../_services/toast-service'
import { useState, useEffect } from 'preact/hooks'
import ChristmasHorizontal from "../../assets/christmas_horizontal.png"
import ChristmasVertical from "../../assets/christmas_vertical.png"

import './sales-banner.scss'

export default function SalesBanner({
    badge,
    buttonLabel,
    colorPallete,
    disable,
    discountBig,
    discountLabel,
    discountSmall,
    discountTitle,
    endDate,
    image,
    imgAlt,
    madButtonLabel,
    modalTitle,
    startDate,
    studentMeta,
    title,
    user
}) {
    /** Variables **/
    const isPremiumUser = studentMeta?.has_whatsapp && studentMeta?.shop_id !== 24
    const isMad = studentMeta.formative_zone === 'MAD'
    const semesterId = isMad ? 90 : 84
    const isSpanish = isSpanishUser(studentMeta.shop_id)
    const isOnline = studentMeta.shop_id === 1

    /** States **/
    const [timeLeft, setTimeLeft] = useState({
        days: '00',
        hours: '00',
        minutes: '00',
        seconds: '00'
    })
    const [showModal, setShowModal] = useState(false)
    const [loadingSkeleton, setLoadingSkeleton] = useState(false)
    const [showSalesBannerBanner, setShowSalesBannerBanner] = useState(false)
    const [loadingContinue, setLoadingContinue] = useState(false)
    const [cartData, setCartData] = useState(null)
    const [premiumSelected, setPremiumSelected] = useState(isPremiumUser)
    const [selectedProducts, setSelectedProducts] = useState([])
    const [cartDataAlreadyLoaded, setCartDataAlreadyLoaded] = useState()
    const [toValidate, setToValidate] = useState()
    const [generatedMadCartURL, setGeneratedMadCartURL] = useState(null)

    /** Lifcycles **/
    useEffect(() => {
        updateCountdown()
        const interval = setInterval(updateCountdown, 1000)
        return () => clearInterval(interval)
    }, [])

    useEffect(() => {
        (async () => {
            const result = await checkSalesBanner()
            setShowSalesBannerBanner(result)
        })()
    }, [])

    /** Methods **/
    const normalizeDate = (date) => {
        return new Date(date.year, date.month - 1, date.day)
    }

    const startDateIsBeforeEndDay = () => {
        const start = normalizeDate(startDate)
        const end = normalizeDate(endDate)
        const now = Date.now()

        return now >= start.getTime() && now <= end.getTime()
    }

    const checkSalesBanner = () => {
        if (!isSpanish || !isOnline || isMad) return false

        try {
            const zoneCode = studentMeta.formative_zone_code

            const program = (studentMeta.unfinished_academic_programs || [])
                .find(p => p.formative_zone_code === zoneCode)

            if (!program?.formative_actions) return false

            const formativeActions = program.formative_actions

            const allSubjects = formativeActions
                .map(formativeAction => formativeAction?.shop_code?.trim())
                .filter(code => code !== '')

            const studentSubjects = (studentMeta.coursing_or_finished_enrolls || [])
                .filter(enroll => enroll.formative_zone_code === zoneCode)
                .map(enroll => enroll?.formative_action?.shop_code?.trim())
                .filter(code => code !== '')

            const missing = allSubjects.filter(code => {
                const formativeAction = formativeActions.find(a => a?.shop_code?.trim() === code)

                if (formativeAction?.is_enrollable === false) return false

                return !studentSubjects.includes(code)
            })

            return missing.length >= 2

        } catch (error) {
            console.error('Error en checkSalesBanner:', error)
            return false
        }
    }

    const updateCountdown = () => {
        const now = Date.now()
        const end = normalizeDate(endDate).getTime()

        const distance = end - now

        if (distance <= 0) {
            setTimeLeft({
                days: '00',
                hours: '00',
                minutes: '00',
                seconds: '00'
            })
            return
        }

        const days = Math.floor(distance / (1000 * 60 * 60 * 24))
        const hours = Math.floor((distance / (1000 * 60 * 60)) % 24)
        const minutes = Math.floor((distance / (1000 * 60)) % 60)
        const seconds = Math.floor((distance / 1000) % 60)

        setTimeLeft({
            days: String(days).padStart(2, '0'),
            hours: String(hours).padStart(2, '0'),
            minutes: String(minutes).padStart(2, '0'),
            seconds: String(seconds).padStart(2, '0'),
        })
    }

    const encryptString = (studentId) => {
        const cipher = crypto.createCipheriv(
            micuentaenv.ENCRYPTION_METHOD,
            adaptKey(micuentaenv.API_ADMIN_KEYWORD),
            micuentaenv.IV_ENCRYPTION_STRING
        )
        return encodeURIComponent(Buffer.from(
            cipher.update(studentId, 'utf8', 'base64') + cipher.final('base64')
        ).toString())
    }

    const adaptKey = (key) => {
        const len = 32
        if (key.length >= len) {
            return key.substring(0, len)
        } else {
            return key + Array(len - key.length + 1).join('\x00')
        }
    }

    const openModal = async () => {
        if (isMad) {
            if (loadingContinue) return
            setLoadingContinue(true)
            const cart = await getCart()
            if (cart.cartUrl) setGeneratedMadCartURL(cart.cartUrl)
            setLoadingContinue(false)
            return
        }

        setShowModal(true)
    }

    const openNewWindow = () => {
        generatedMadCartURL && window.open(generatedMadCartURL, '_blank')
    }

    const getCart = async (products = []) => {
        try {
            let baseUrl =
                `${micuentaenv.STUDENT_ACCOUNT_API_basePath}/get_recommended_cart_black_friday` +
                `?s=${encryptString(user.id.toString())}` +
                `&as=${semesterId}`

            if (products && Array.isArray(products) && products.length > 0) {
                const formattedProducts = products
                    .filter(p => !!p)
                    .join(',')
                const productsEncrypted = encryptString(formattedProducts)
                baseUrl += `&fa=${productsEncrypted}`
            } else {
                const emptyEncrypted = encryptString('')
                baseUrl += `&fa=${emptyEncrypted}`
            }

            const res = await fetch(baseUrl, {
                headers: { accept: 'application/json' },
                method: 'GET'
            })

            const data = await res.json()

            if (data.code && data.code !== 200) {
                throw new Error(data.message || 'Ha ocurrido un error')
            }

            return data
        } catch (error) {
            console.error('Error en getCart:', error?.message)
            toastService.showToast({ message: 'Se ha producido un error al cargar el carrito', type: 'danger' })
            setShowModal(false)
            return null
        }
    }

    const handleCloseModal = () => {
        setCartDataAlreadyLoaded(true)
        setShowModal(false)
    }

    const handleCalculateDiscount = async (products, initial) => {
        if (initial) {
            setLoadingSkeleton(true)
            const cart = await getCart()
            setLoadingSkeleton(false)

            const recommendedIds = getRecommendedProducts(cart)
            setSelectedProducts(recommendedIds)

            setCartData(cart)
        } else {
            setLoadingContinue(true)
            const cart = await getCart(products)
            setLoadingContinue(false)

            setCartData(cart)
        }
    }

    const handleContinue = async () => {
        if (cartData.cartUrl) {
            window.open(cartData.cartUrl, '_blank')
        }
    }

    const getRecommendedProducts = (cart) => {
        if (!cart?.semesters) return []
        return cart.semesters?.flatMap(({ products }) => {
            return products
                .filter((product) => product.price > 0)
                .filter((product) => product.is_recommended)
                .map((product) => product.id_product)
        })
    }

    const splitDiscount = (text) => {
        const words = text.split(" ")
        return {
            first: words.slice(0, 3).join(" "),
            rest: words.slice(3).join(" ")
        }
    }

    /** Return **/
    return (
        !disable && showSalesBannerBanner && startDateIsBeforeEndDay() && (
            <Fragment>
                <div className={`${colorPallete} sales-banner-card d-flex flex-column lg-flex-row flex-left`}>
                    {colorPallete === 'christmas' &&
                        <div className="sales-banner-card__christmas-overlay d-flex flex-column flex-space-between">
                            <div className="d-flex flex-space-between">
                                <img className="sales-banner-card__christmas-overlay__horizontal-pattern" src={ChristmasHorizontal} alt="Christmas Border" />
                                <img className="sales-banner-card__christmas-overlay__horizontal-pattern sales-banner-card__christmas-overlay__horizontal-pattern--right" src={ChristmasHorizontal} alt="Christmas Border" />
                            </div>
                            <div className="sales-banner-card__christmas-overlay__center-element d-flex flex-space-between">
                                <img className="sales-banner-card__christmas-overlay__vertical-pattern" src={ChristmasVertical} alt="Christmas Border" />
                                <img className="sales-banner-card__christmas-overlay__vertical-pattern sales-banner-card__christmas-overlay__vertical-pattern--reverted" src={ChristmasVertical} alt="Christmas Border" />
                            </div>
                            <div className="d-flex flex-space-between">
                                <img className="sales-banner-card__christmas-overlay__horizontal-pattern" src={ChristmasHorizontal} alt="Christmas Border" />
                                <img className="sales-banner-card__christmas-overlay__horizontal-pattern sales-banner-card__christmas-overlay__horizontal-pattern--right" src={ChristmasHorizontal} alt="Christmas Border" />
                            </div>
                        </div>
                    }
                    <div className='d-flex flex-column lg-flex-row gap-3'>
                        {image
                            ? <img src={image} alt={imgAlt} className='sales-banner-card__image full-width' />
                            : <div className="d-flex flex-column full-width">
                                {discountTitle && <p className="no-margin m-bottom-half">{discountTitle}</p>}
                                {discountBig && discountSmall &&
                                    <div className="sales-banner-card__left-element">
                                        <span className="sales-banner-card__discount-big font-48 font-bold line-height-48">{discountBig}</span>
                                        <span className="font-24 font-bold line-height-24 m-l-half">{discountSmall}</span>
                                    </div>
                                }
                                {discountLabel && (() => {
                                    const { first, rest } = splitDiscount(discountLabel)
                                    return (
                                        <div className="sales-banner-card__discount-label font-38 font-bold line-height-38">
                                            <span className="sales-banner-card__discount-label--hilight">{first}</span>
                                            {rest && <span> {rest}</span>}
                                        </div>
                                    )
                                })()}
                            </div>
                        }
                        <div className='sales-banner-card__center-gap d-flex flex-column gap-2 flex-space-between'>
                            <h2 className='sales-banner-card__title no-margin'>
                                <strong>{title}</strong>
                            </h2>
                            {badge &&
                                <div className='width-fit-content'>
                                    <div className='sales-banner-card__badge font-14'>
                                        <strong>{badge}</strong>
                                    </div>
                                </div>
                            }
                        </div>
                        <div className='d-flex flex-column gap-1 flex-space-between full-width'>
                            <div className='sales-banner-card__countdown d-flex flex-center-x gap-1 no-margin'>
                                <div>
                                    <p className='sales-banner-card__ends-in no-margin nowrap'><strong>{t('Acaba en...')}</strong></p>
                                </div>
                                <div className='d-flex flex-center-y gap-1 full-width'>
                                    <div>
                                        <p className='no-margin t-right font-26'>{timeLeft.days}</p>
                                        <p className='no-margin t-right font-14'>{t('días')}</p>
                                    </div>
                                    <div>
                                        <p className='no-margin'><strong>:</strong></p>
                                    </div>
                                    <div>
                                        <p className='no-margin t-right font-26'>{timeLeft.hours}</p>
                                        <p className='no-margin t-right font-14'>{t('hrs')}</p>
                                    </div>
                                    <div>
                                        <p className='no-margin'><strong>:</strong></p>
                                    </div>
                                    <div>
                                        <p className='no-margin t-right font-26'>{timeLeft.minutes}</p>
                                        <p className='no-margin t-right font-14'>{t('mins')}</p>
                                    </div>
                                    <div>
                                        <p className='no-margin'><strong>:</strong></p>
                                    </div>
                                    <div >
                                        <p className='no-margin t-right font-26'>{timeLeft.seconds}</p>
                                        <p className='no-margin t-right font-14'>{t('segs')}</p>
                                    </div>
                                </div>
                            </div>
                            <ButtonSpinner
                                showSpinner={loadingContinue}
                                onClick={generatedMadCartURL ? openNewWindow : openModal}
                                btnType='primary'
                                classNameContainer='sales-banner-card__button'
                                className='sales-banner-card__button'
                            >
                                {generatedMadCartURL ? madButtonLabel : buttonLabel}
                            </ButtonSpinner>
                        </div>
                    </div>
                    <div className='d-flex flex-space-between gap-1 flex-bottom gap-1'>
                    </div>
                </div>

                {showModal && (
                    <Modal visible={showModal} closeModal={handleCloseModal} overlayClassName='modal-overlay sales-banner-overlay'>
                        <MatriculacionModal
                            cartData={cartData}
                            cartDataAlreadyLoaded={cartDataAlreadyLoaded}
                            isPremiumUser={isPremiumUser}
                            loadingContinue={loadingContinue}
                            loadingSkeleton={loadingSkeleton}
                            onCalculateDiscount={handleCalculateDiscount}
                            onCloseModal={handleCloseModal}
                            onContinue={handleContinue}
                            premiumSelected={premiumSelected}
                            selectedProducts={selectedProducts}
                            setLoadingContinue={setLoadingContinue}
                            setPremiumSelected={setPremiumSelected}
                            setSelectedProducts={setSelectedProducts}
                            setToValidate={setToValidate}
                            title={modalTitle}
                            toValidate={toValidate}
                        />
                    </Modal>
                )}
            </Fragment>
        )
    )
}

